import { Temporal } from "@js-temporal/polyfill";
import type {
  WeatherForCurrent5DaysWindow, Location,
  WeatherCondition,
} from "@/types";

export const POPULAR_CITIES: Location[] = [
  { id: "pop-mnl", name: "Manila", region: "Philippines", lat: 14.6, lon: 120.98 },
  { id: "pop-sgn", name: "Ho Chi Minh City", region: "Vietnam", lat: 10.8, lon: 106.7 },
  { id: "pop-sin", name: "Singapore", region: "Singapore", lat: 1.3, lon: 103.8 },
  { id: "pop-bkk", name: "Bangkok", region: "Thailand", lat: 13.7, lon: 100.5 },
  { id: "pop-cdmx", name: "Mexico City", region: "Mexico", lat: 19.4, lon: -99.1 },
  { id: "pop-mad", name: "Madrid", region: "Spain", lat: 40.4, lon: -3.7 },
  { id: "pop-lis", name: "Lisbon", region: "Portugal", lat: 38.7, lon: -9.1 },
  { id: "pop-lon", name: "London", region: "United Kingdom", lat: 51.5, lon: -0.13 },
  { id: "pop-ber", name: "Berlin", region: "Germany", lat: 52.5, lon: 13.4 },
  { id: "pop-par", name: "Paris", region: "France", lat: 48.86, lon: 2.35 },
  { id: "pop-nyc", name: "New York", region: "United States", lat: 40.7, lon: -74.0 },
  { id: "pop-sf", name: "San Francisco", region: "United States", lat: 37.77, lon: -122.42 },
  { id: "pop-la", name: "Los Angeles", region: "United States", lat: 34.05, lon: -118.24 },
  { id: "pop-tyo", name: "Tokyo", region: "Japan", lat: 35.68, lon: 139.69 },
  { id: "pop-syd", name: "Sydney", region: "Australia", lat: -33.86, lon: 151.2 },
];

export function detectLocation(): Promise<Location> {
  return new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) {
      reject(new Error("Geolocation not supported"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        id: `geo-${pos.coords.latitude.toFixed(3)}-${pos.coords.longitude.toFixed(3)}`,
        name: "Your location",
        region: `${pos.coords.latitude.toFixed(2)}, ${pos.coords.longitude.toFixed(2)}`,
        lat: pos.coords.latitude,
        lon: pos.coords.longitude,
      }),
      (err) => reject(err),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 },
    );
  });
}

interface GeocodingResult {
  id?: number | string;
  name: string;
  latitude: number;
  longitude: number;
  admin1?: string;
  country?: string;
}

export async function searchCities(query: string): Promise<Location[]> {
  const q = (query ?? "").trim();
  if (q.length < 2) return [];
  const url = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(q)}&count=10&language=en&format=json`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Geocoding failed: ${res.status}`);
  const json = (await res.json()) as { results?: GeocodingResult[] };
  const results = json.results ?? [];
  return results.map((r) => ({
    id: `geo-${r.id ?? `${r.latitude}-${r.longitude}`}`,
    name: r.name,
    region: [r.admin1, r.country].filter(Boolean).join(", ") || (r.country ?? ""),
    lat: r.latitude,
    lon: r.longitude,
  }));
}

export const COND_LABELS: Record<WeatherCondition, string> = {
  sun: "Sunny",
  night: "Clear night",
  cloud: "Cloudy",
  rain: "Rain",
  storm: "Storm",
  heat: "Heatwave",
  cold: "Cold",
  wind: "Windy",
  frost: "Frost",
  mist: "Misty",
};

export function weatherCareSummary(forecast: WeatherForCurrent5DaysWindow | null): string | null {
  if (!forecast) return null;
  const today = forecast.today;
  const currentDate = Temporal.Now.zonedDateTimeISO();
  const c = today.hours[currentDate.hour]!!.condition;
  const windKmhPeak = Math.max(...today.hours.map(h => h.windKmh));
  const uvIndexPeak = Math.max(...today.hours.map(h => h.uvIndex));
  if (c === "frost") return "Frost tonight — bring outdoor pots in";
  if (c === "storm") return "Storms today — rain will do the watering";
  if (c === "rain") return "Rain today — outdoor watering skipped";
  if (c === "heat") return "Heatwave — outdoor pots need extra water";
  if (uvIndexPeak >= 9) return "Strong sun — shade sensitive plants";
  if (windKmhPeak >= 30) return "Windy — secure tall outdoor plants";
  return null;
}
